import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { TileEmpty } from '../TileWrapper';
import { AlertCircle, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

// Mock data for simulation
const MOCK_DATA = {
  count: 8,
  items: [
    { id: '1', userName: 'Tom Wilson', userId: '1', requirementName: 'Asbestos Awareness', expiredDays: 15 },
    { id: '2', userName: 'Lisa Davis', userId: '2', requirementName: 'PASMA', expiredDays: 8 },
    { id: '3', userName: 'James Taylor', userId: '3', requirementName: 'Confined Spaces', expiredDays: 3 },
  ],
};

export function ExpiredRequirementsTile({ filter, size, useMockData }) {
  const navigate = useNavigate();
  const { company } = useAuth();
  const [data, setData] = useState({ count: 0, items: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (useMockData) {
      setData(MOCK_DATA);
      setLoading(false);
      return;
    }

    if (!company?.id) return;

    const loadData = async () => {
      setLoading(true);
      try {
        const today = new Date().toISOString().split('T')[0];

        let query = supabase
          .from('qualifications')
          .select(`
            id,
            expiry_date,
            user_profiles!inner(id, first_name, last_name, company_id),
            requirements_library(id, name)
          `, { count: 'exact' })
          .eq('user_profiles.company_id', company.id)
          .not('expiry_date', 'is', null)
          .lt('expiry_date', today)
          .order('expiry_date', { ascending: true })
          .limit(size === 'medium' ? 5 : 3);

        const { data: qualifications, count, error } = await query;

        if (error) throw error;

        const items = (qualifications || []).map(q => ({
          id: q.id,
          userName: `${q.user_profiles.first_name} ${q.user_profiles.last_name}`,
          userId: q.user_profiles.id,
          requirementName: q.requirements_library?.name || 'Unknown',
          expiredDays: Math.ceil((new Date() - new Date(q.expiry_date)) / (1000 * 60 * 60 * 24)),
        }));

        setData({ count: count || 0, items });
      } catch (err) {
        console.error('Failed to load expired data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [company?.id, filter?.spaceId, size, useMockData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-center">
          <div className="w-16 h-16 rounded-full bg-gray-200 mx-auto mb-2" />
          <div className="h-4 w-20 bg-gray-200 rounded mx-auto" />
        </div>
      </div>
    );
  }

  if (data.count === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <div className="bg-green-100 p-3 rounded-full mb-2">
          <XCircle className="h-6 w-6 text-green-600" />
        </div>
        <p className="text-sm font-medium text-green-700">All Clear</p>
        <p className="text-xs text-gray-500">No expired requirements</p>
      </div>
    );
  }

  if (size === 'small') {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <div className="bg-red-100 p-3 rounded-full mb-2">
          <AlertCircle className="h-6 w-6 text-red-600" />
        </div>
        <p className="text-3xl font-bold text-red-600">{data.count}</p>
        <p className="text-sm text-gray-600">Expired</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-3">
        <Badge variant="destructive" className="text-lg px-3 py-1">
          {data.count} Expired
        </Badge>
      </div>

      {data.items.map((item) => (
        <div
          key={item.id}
          onClick={() => navigate(`/profile/${item.userId}`)}
          className="flex items-center justify-between p-2 rounded-lg hover:bg-red-50 cursor-pointer transition-colors border border-red-100"
        >
          <div className="min-w-0">
            <p className="text-sm font-medium truncate">{item.requirementName}</p>
            <p className="text-xs text-gray-500 truncate">{item.userName}</p>
          </div>
          <span className="text-xs text-red-600 shrink-0">
            {item.expiredDays}d ago
          </span>
        </div>
      ))}

      {data.count > data.items.length && (
        <button
          onClick={() => navigate('/qualifications')}
          className="w-full text-center text-sm text-red-600 hover:text-red-700 py-2"
        >
          +{data.count - data.items.length} more
        </button>
      )}
    </div>
  );
}
